import type { SelectOption } from '~/components/ui/inputs/selectOptions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SavedViewsFilterControls } from '~/components/ui/SavedViewsFilterControls';
import { requestForQuoteFilterFields } from '~/pages/my-requests-for-quote/lib/requestForQuoteFilterFields';
import {
  activeRowCount,
  isArchivedRow,
  withoutArchivedRows,
  type RequestForQuoteFilterRow,
} from '~/pages/my-requests-for-quote/lib/requestForQuoteFilters';
import type { RequestForQuoteSavedViews } from '~/pages/my-requests-for-quote/lib/useRequestForQuoteSavedViews';
import { RequestForQuoteFilterPanel } from './RequestForQuoteFilterPanel';

type RequestForQuoteToolbarProps = {
  readonly products: SelectOption[];
  readonly sellers: SelectOption[];
  readonly creators: SelectOption[];
  readonly isLoadingOptions: boolean;
  readonly isArchivedFilterAvailable: boolean;
  readonly views: RequestForQuoteSavedViews;
};

export function RequestForQuoteToolbar({
  products,
  sellers,
  creators,
  isLoadingOptions,
  isArchivedFilterAvailable,
  views,
}: RequestForQuoteToolbarProps) {
  const { t } = useTranslation('my-requests-for-quote');
  const fieldOptions = useMemo(
    () => requestForQuoteFilterFields(t, isArchivedFilterAvailable),
    [t, isArchivedFilterAvailable]
  );
  const visibleRows = isArchivedFilterAvailable
    ? views.rows
    : withoutArchivedRows(views.rows);
  const changeVisibleRows = (nextRows: RequestForQuoteFilterRow[]) =>
    views.setRows(
      isArchivedFilterAvailable
        ? nextRows
        : [...nextRows, ...views.rows.filter(isArchivedRow)]
    );

  return (
    <div
      className="flex items-center justify-end gap-4 p-2 px-4"
      data-testid="request-for-quote-toolbar"
    >
      <SavedViewsFilterControls
        namespace="my-requests-for-quote"
        keyPrefix="my_requests_for_quote.filter"
        views={views}
        activeCount={activeRowCount(visibleRows)}
        visibleRowCount={visibleRows.length}
        buttonTestId="request-for-quote-filter-button"
        countTestId="request-for-quote-filter-count"
        renderPanel={({ maxHeight, bottomContent }) => (
          <RequestForQuoteFilterPanel
            maxHeight={maxHeight}
            bottomContent={bottomContent}
            rows={visibleRows}
            fieldOptions={fieldOptions}
            productOptions={products}
            sellerOptions={sellers}
            creatorOptions={creators}
            isLoadingOptions={isLoadingOptions}
            onRowsChange={changeVisibleRows}
            onClearAll={() => views.setRows([])}
          />
        )}
      />
    </div>
  );
}
